// Typed wrapper around the sfconsole REST API. One place to change if
// the backend JSON shape shifts — every page imports from here so a
// field rename turns into a TypeScript compile error, not a runtime 500.

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

export interface Health {
  status: string;
  uptime: string;
  db: boolean;
  version: string;
}

export interface User {
  username: string;
  role: "admin" | "operator" | "readonly";
}

export interface Sensor {
  id: number;
  name: string;
  enabled: boolean;
  protocol: string;
  listen_port: number;
  zone_id: number;
  /** All zones assigned to this sensor (includes primary). Added for multi-zone. */
  zone_ids?: number[];
  listen_ip: string;
  exporter_ip: string;
  /** Manual sampling-rate override; 0 = auto-detect from the wire. */
  sampling_rate?: number;
  /** SNMP credentials used by /discover-interfaces (Add Interface autofill). */
  snmp_community?: string;
  snmp_version?: string;
  snmp_port?: number;
  /** Per-sensor chart colour "#rrggbb"; empty/undefined = palette fallback. */
  color?: string;
  /**
   * Wansight "IP Validation" mode (Plan P1.1). Controls whether a flow
   * is counted based on whether src/dst IP is inside the configured
   * IP Zone. Default "on" matches Wansight's recommended setting.
   */
  ip_validation_mode?: "off" | "on" | "strict" | "exclusive";
  /**
   * Wansight "Stats Engine" mode (Plan P1.2). Controls how much per-flow
   * aggregation work the sensor does (basic = lowest RAM; extended =
   * default; full = + external-IP tops, RAM ×3-5).
   */
  stats_engine?: "basic" | "extended" | "full";
  /**
   * Per-sensor IXP/NIX classification override. "auto" (default) decides
   * each flow's class by GeoIP (VN remote → NIX, else IXP). "ixp"/"nix"
   * force every flow on this sensor into that bucket and skip GeoIP —
   * use when IXP and NIX are split onto separate switch ports / sensors.
   */
  traffic_class?: "auto" | "ixp" | "nix";
  /**
   * NetFlow active timeout in seconds (Plan P1.3). Should match the
   * exporting router's `flow active-timeout`. Default 60. The aggregator
   * uses 2× this value to clamp pro-rate duration when a record arrives
   * with a clock-drifted FlowStart.
   */
  flows_timeout_active?: number;
  /**
   * NetFlow inactive timeout in seconds (Plan P1.3). Default 30. Match
   * the router's `flow inactive-timeout` for the most accurate bin
   * placement of short-lived idle-flushed flows.
   */
  flows_timeout_inactive?: number;
  /**
   * Aggregator bin width (P3.2). Allowed values: 5 / 20 / 60 seconds.
   * Default 5 — matches the legacy hardcoded behaviour. Increase only
   * on >10-sensor / >500k-IP-zone deployments where InfluxDB write
   * rate is a bottleneck. Multi-sensor charts clamp the GROUP BY
   * width to the largest configured granularity to keep buckets
   * dense.
   */
  granularity_seconds?: 5 | 20 | 60;
  /** SNMP sysUpTime in seconds; 0 / undefined = SNMP not configured or unreachable. */
  uptime_sec?: number;
}

/** One row from `GET /api/v1/sensors/{id}/discover-interfaces`. */
export interface DiscoveredIface {
  if_index: number;
  name: string;
  description: string;
  speed_bps: number;
  oper_status: string;
}

export interface SensorInterface {
  if_index: number;
  name: string;
  direction: string;
  color: string;
  stats_mode?: string;
  /** Link speed inbound, bits/sec; 0 = unknown. */
  speed_in?: number;
  /** Link speed outbound, bits/sec; 0 = unknown. */
  speed_out?: number;
}

export interface SensorDetailResp {
  sensor: Sensor;
  interfaces: SensorInterface[];
}

export interface SensorWriteReq {
  name: string;
  enabled: boolean;
  listen_ip: string;
  listen_port: number;
  protocol: "auto" | "sflow" | "nfv5" | "nfv9" | "ipfix";
  exporter_ip: string;
  zone_id: number;
  /** Additional zones (optional). Backend merges these with zone_id as primary. */
  zone_ids?: number[];
  spool_dir?: string;
  /** Per-sensor chart colour "#rrggbb"; empty = palette fallback. */
  color?: string;
  /** Manual sampling-rate override; 0 = auto-detect from the wire. */
  sampling_rate?: number;
  snmp_community?: string;
  snmp_version?: string;
  snmp_port?: number;
  /** See {@link Sensor.ip_validation_mode}. */
  ip_validation_mode?: "off" | "on" | "strict" | "exclusive";
  /** See {@link Sensor.stats_engine}. */
  stats_engine?: "basic" | "extended" | "full";
  /** See {@link Sensor.traffic_class}. Defaults to "auto" if undefined. */
  traffic_class?: "auto" | "ixp" | "nix";
  /** See {@link Sensor.flows_timeout_active}. Defaults to 60 if 0/undefined. */
  flows_timeout_active?: number;
  /** See {@link Sensor.flows_timeout_inactive}. Defaults to 30 if 0/undefined. */
  flows_timeout_inactive?: number;
  /** See {@link Sensor.granularity_seconds}. Defaults to 5 if 0/undefined. */
  granularity_seconds?: 5 | 20 | 60;
  interfaces: Array<{
    if_index: number;
    name: string;
    direction: "auto" | "in" | "out";
    color: string;
    stats_mode?: "basic" | "extended";
    /** Link speed inbound, bits/sec; 0 = unknown. */
    speed_in?: number;
    /** Link speed outbound, bits/sec; 0 = unknown. */
    speed_out?: number;
  }>;
}

export interface Decoder {
  id: number;
  name: string;
  color: string;
  enabled: boolean;
}

export interface Zone {
  id: number;
  name: string;
  has_ipv6: boolean;
}

export interface ZoneNode {
  id: number;
  parent_id: number;
  prefix: string;
  description: string;
  graphs: boolean;
  accounting: boolean;
  /** Wansight "IP Group" label (auto-resolved from ipgroup.name).
   *  Empty when the prefix has no ipgroup_id assigned. */
  ip_group?: string;
}

export interface IPAddrWriteReq {
  prefix: string;
  parent_id?: number;
  description?: string;
  graphs: boolean;
  accounting: boolean;
  /** Type a customer label or pick an existing one — backend creates
   *  the ipgroup row on demand. Empty string clears the assignment. */
  ip_group?: string;
}

export interface IPGroup {
  id: number;
  name: string;
}

/** Wire shape of /api/v1/live/totals — feeds the dashboard's 4 big
 *  realtime cards. trend is the percent change between the recent half
 *  and the older half of the live rolling window (positive = rising). */
export interface LiveTotals {
  bps_in: number;
  bps_out: number;
  pps_in: number;
  pps_out: number;
  /** Unix seconds at the latest sample bin start. The dashboard uses
   *  this to detect "sensor offline" (Bug 4) — if Date.now() - as_of
   *  exceeds 30 s the data is stale; > 5 min suggests sensor death. */
  as_of: number;
  trend: {
    bps_in: number;
    bps_out: number;
    pps_in: number;
    pps_out: number;
  };
}

/** Wire shape of POST /api/v1/flows/search request body. Plan P2.1
 *  — Wansight's "Reports » Tools » Flows" search across the binary
 *  flow archive (wgflow.* files written by sfsensor). */
export interface FlowSearchReq {
  sensor_id: number;
  start_unix: number;
  end_unix: number;
  src_ip?: string;
  dst_ip?: string;
  /** Match against either src or dst — operator convenience. */
  ip?: string;
  /** Named decoder filter (e.g. "DNS", "HTTPS"). When set, overrides protocol. */
  decoder_name?: string;
  protocol?: number;
  src_port?: number;
  dst_port?: number;
  /** Match against either src or dst port. */
  port?: number;
  min_bytes?: number;
  /** Returned-row cap. Default 200, server max 5000. */
  limit?: number;
}

/** One row of the flow-search result table. Mirrors flowfile.Record
 *  but with string IPs and unix-seconds timestamps for the UI. */
export interface FlowSearchRow {
  ts_unix: number;
  src_ip: string;
  dst_ip: string;
  src_port: number;
  dst_port: number;
  protocol: number;
  tcp_flags: number;
  bytes: number;
  packets: number;
  sampling_rate: number;
  input_if?: number;
  output_if?: number;
  src_as?: number;
  dst_as?: number;
}

/** Wire shape of POST /api/v1/flows/search response body. */
export interface FlowSearchResp {
  rows: FlowSearchRow[];
  /** Total records read pre-filter (≥ matched). High scanned with
   *  low matched suggests the filters could be tighter. */
  scanned: number;
  /** Records that passed the filter. May exceed rows.length when the
   *  result set was truncated by limit. */
  matched: number;
  files_read: number;
  /** True when scanCap or limit clipped the result. UI should warn. */
  truncated: boolean;
  duration_ms: number;
  spool_dir: string;
}

/** One row of /api/v1/live/sensors-summary — current rolling-window
 *  rates per sensor, used by the Sensors page table. */
export interface LiveSensorSummary {
  sensor_id: number;
  name: string;
  bps_in: number;
  bps_out: number;
  pps_in: number;
  pps_out: number;
  /** Unix seconds at the latest sample bin start observed for this
   *  sensor. Drives the per-row freshness indicator (green/yellow/red
   *  dot) in the Sensors table. */
  as_of: number;
  online: boolean;
}

export interface IPRates {
  IPNodeID: number;
  Direction: number;
  BPS: number;
  PPS: number;
  UpdatedAt: string;
}

export interface IPNode {
  id: number;
  prefix: string;
  description: string;
  match: "exact" | "parent" | "child" | "host" | "range" | "zone";
  /** Non-empty when match === "host": the raw IP for per-host series queries. */
  addr?: string;
}

export interface IPSearchResp {
  query: string;
  nodes: IPNode[];
}

export interface IPSeriesResp {
  node_id: number;
  prefix: string;
  description: string;
  points: SeriesPoint[];
}

export interface IPHostSeriesResp {
  addr: string;
  points: SeriesPoint[];
}

export interface IPCIDRSeriesResp {
  prefix: string;
  points: SeriesPoint[];
}

// ── Notification types ────────────────────────────────────────────────────────

export interface NotifChannel {
  id: number;
  name: string;
  type: "telegram" | "webhook";
  config: Record<string, string>;
  enabled: boolean;
  created_at: string;
}

export type AlertMetric = "bps_in" | "bps_out" | "pps_in" | "pps_out";
export type AlertTargetType = "global" | "subnet" | "ip";
export type AlertStatus = "ok" | "pending" | "firing";

export interface AlertRule {
  id: number;
  name: string;
  enabled: boolean;
  target_type: AlertTargetType;
  target_value: string;       // "" for global, "1.2.3.0/24" for subnet
  metric: AlertMetric;
  threshold: number;
  sustained_s: number;
  cooldown_s: number;
  notify_resolve: boolean;
  channel_ids: number[];      // channels this rule fires to (one event fans out to all)
  channel_names: string[];    // read-only, parallel to channel_ids
  channel_id?: number;        // legacy single channel (read-only primary); kept for backward compat
  channel_name?: string;      // read-only, name of the primary channel
  status: AlertStatus;        // read-only, from alert_state
  last_fired: string;         // read-only, ISO or ""
  message_template: string;   // Go text/template; "" = built-in format
  zone_filter_ids:   number[];  // empty = all zones; only for target_type="ip"
  zone_filter_names: string[];  // read-only, parallel to ids
  geo_class: string;          // "" = total | "ixp" = international | "nix" = domestic
  /** Per-protocol filter — when set, only flows matching this decoder count
   * toward the threshold (FastNetMon-style "TCP+SYN > X" rules). null = aggregate
   * across all protocols (legacy behavior). When set, geo_class is ignored
   * because the per-protocol rolling store is geo-agnostic. */
  decoder_id:   number | null;
  decoder_name: string;        // read-only, resolved name e.g. "TCP+SYN"
  /** Forensics blob captured the last time this rule fired — top talkers +
   * protocol breakdown. Optional/undefined for never-fired rules. */
  forensics?: AlertForensics;
  /** Active-window: rule only fires when wall-clock (in tz) is between
   * start..end. Empty start/end = always active. start > end spans midnight. */
  active_window_start: string; // "HH:MM" or ""
  active_window_end:   string; // "HH:MM" or ""
  active_window_tz:    string; // IANA tz; "" → backend uses "Asia/Ho_Chi_Minh"
  /** Extra seconds to hold before sending RESOLVED. Webhook channels only;
   * 0 = send immediately after the sustained window (default). */
  resolve_delay_s: number;
  /** CDN exclusion: when true, traffic to/from Google (resp. Cloudflare)
   * is subtracted from the metric before the threshold check. Only
   * meaningful for target_type="ip" rules. */
  exclude_google: boolean;
  exclude_cloudflare: boolean;
}

/** Forensics snapshot captured at fire time — answers "who/what caused
 * the spike?" without scrolling the dashboard. Mirrors the Go Forensics
 * struct in internal/notify/forensics.go. */
export interface AlertForensics {
  captured_at: number;     // unix seconds
  metric: string;
  threshold: number;
  value: number;
  decoder_id?: number;
  decoder?: string;
  geo_class?: string;
  top_talkers: Array<{
    addr: string;
    bps: number;
    pps: number;
    pct: number;
  }>;
  top_protos: Array<{
    decoder: string;
    bps: number;
    pps: number;
    pct: number;
  }>;
}

export interface NotifHistoryItem {
  id: number;
  rule_id: number;
  rule_name: string;
  channel_id: number;
  channel_name: string;
  event: "firing" | "resolved" | "test";
  value: number;
  message: string;
  sent_at: string;
  ok: boolean;
  error: string;
  /** Forensics captured at fire time. Present on firing entries that ran
   * after the v1.4 forensics rollout; older entries leave it undefined. */
  forensics?: AlertForensics;
}

export interface TopTalker {
  rank: number;
  node_id: number;
  prefix: string;
  desc: string;
  bps: number;
  pps: number;
  pct: number;
  /** ipgroup.name for the prefix this IP belongs to. Empty when unset. */
  group?: string;
  /** ipzone.name containing the matched prefix. Used as fallback label
   *  when group is empty. */
  zone?: string;
}

/**
 * Optional scope filter passed to `api.liveTop()`. All fields are AND-ed
 * server-side and compose with the existing geo/dir/metric/limit knobs.
 * Use this from the IP Top page to narrow down to a CIDR / ipgroup /
 * zone-node without adding new endpoints.
 */
export interface TopFilter {
  /** CIDR like "10.0.0.0/8" — only IPs inside this prefix. */
  cidr?: string;
  /** ipgroup.id — only IPs whose matched prefix has this group_id. */
  ipgroup?: number;
  /** ipaddr.id — only IPs inside this zone-node's range (descendants included). */
  node?: number;
  /** ipzone.id — only IPs whose matched prefix belongs to this zone. */
  zone?: number;
}

export interface SeriesPoint {
  t: number; // unix seconds
  in: number; // bps
  out: number; // bps
}

// ── Update types ──────────────────────────────────────────────────────────────

export interface UpdateCheck {
  current: string;
  latest: string;
  has_update: boolean;
  changelog: string;
  released_at: string;
}

export interface UpdateStatus {
  state: "idle" | "downloading" | "applying" | "done" | "error";
  message: string;
  version: string;
}

// ── external API types ────────────────────────────────────────────────────────

export interface APIKeyRow {
  id: number;
  name: string;
  key_prefix: string;                    // e.g. "sfk_abc1"
  scopes: Array<"read" | "write" | "admin">;
  created_by: number;
  created_at: number;                    // unix seconds
  expires_at: number | null;             // unix seconds, null = never expires
  last_used_at: number | null;
  last_used_ip?: string;
  enabled: boolean;
}

export interface CreateAPIKeyResp {
  key: APIKeyRow;
  full: string; // the full `sfk_xxxx…` plaintext — shown to the user exactly once
}

export interface APISettings {
  rate_limit_rps: number;
  rate_limit_burst: number;
  audit_retention_days: number;
}

/** Live top-talker rolling window settings */
export interface LiveSettings {
  window_secs: number; // 60–600 s
  /** Rollup mode:
   *   "mean" — total bytes ÷ window (smooth average)
   *   "top6" — mean of 6 highest bins (sustained-burst detector, ~30s) */
  window_mode: "mean" | "top6";
  /** Default time range for the dashboard global chart on first load. */
  chart_default_range_secs: number;
  /** Default auto-refresh interval (ms). 0 = off. */
  chart_default_refresh_ms: number;
}

/** Per-sensor protocol analysis — minimum safe rolling window recommendation */
export interface LiveRecommendation {
  min_safe_secs: number;  // computed minimum safe window in seconds
  protocols:     string[]; // distinct protocols of enabled sensors (e.g. ["sflow","nfv5"])
  reason:        string;  // human-readable explanation
}

/** Geo feature flags — mirror of settings_geo.go geoSettingsResp */
export interface GeoSettings {
  ixp_enabled: boolean; // collect international (IXP) traffic
  nix_enabled: boolean; // collect domestic-VN (NIX) traffic
}

/** GeoIP database (MaxMind GeoLite2) status + config */
export interface GeoMMDBStatus {
  license_key_set: boolean;   // true = key stored in DB
  auto_update:     boolean;   // weekly background auto-update enabled
  last_updated:    string;    // RFC3339 or ""
  mmdb_bytes:      number;    // Country.mmdb size, 0 if missing
  mmdb_asn_bytes:  number;    // ASN.mmdb size, 0 if missing
  updating:        boolean;   // download in flight
  last_error?:     string;    // last failure message
}

/** One row from /api/v1/live/top/asn */
export interface TopASNRow {
  rank: number;
  asn: number;
  org: string;
  bps: number;
  pps: number;
  pct: number;
}

export interface GeoMMDBPutBody {
  license_key?: string; // omit or "" = keep existing
  auto_update: boolean;
}

export interface APIAccessLogRow {
  id: number;
  key_id: number;
  ts: number;        // unix seconds
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  ip: string;
}

// ── License types ─────────────────────────────────────────────────────────────

export type LicenseMode = "unlicensed" | "trial" | "licensed" | "grace" | "expired";

export interface LicenseStatus {
  mode: LicenseMode;
  installation_id: string; // send to vendor to get a license key
  customer?: string;
  expires_at?: number;     // unix seconds; undefined when unlicensed
  days_remaining: number;  // positive = days left; negative = overdue
  max_sensors: number;     // 0 = unlimited
  used_sensors: number;
  max_ips: number;         // 0 = unlimited (IP zone entries)
  used_ips: number;
  valid: boolean;          // false = hard block (only /api/v1/license accessible)
  read_only: boolean;
  message: string;
}

/** One envelope point — mean for the smooth fill area, max for the
 *  peak-spike outline drawn on top. Both fields are bps; for native
 *  5 s bins mean === max (no down-sampling distinction). The chart
 *  renders mean as a translucent fill and max as a thin outline,
 *  giving operators the average traffic shape AND short-lived peaks
 *  on the same view. */
export interface EnvelopePoint {
  t: number;
  in_mean: number;
  in_max: number;
  out_mean: number;
  out_max: number;
}

/** One sensor's bandwidth series. Points carry envelope (mean+max) so
 *  the chart can render Wansight-style smooth fill + peak outline. */
export interface SensorSeries {
  sensor_id: string;
  name: string;
  color: string;   // hex color from first interface; empty string = use frontend default palette
  points: EnvelopePoint[];
}

export interface ProtoPoint {
  t: number;
  in:      Record<string, number>;
  out:     Record<string, number>;
  in_pps:  Record<string, number>;
  out_pps: Record<string, number>;
}

export interface IPProtoSeriesResp {
  node_id: number;
  prefix: string;
  description: string;
  decoders: string[];
  points: ProtoPoint[];
}

// ---------------------------------------------------------------------------
// fetch helpers
// ---------------------------------------------------------------------------

// ApiError carries the HTTP status alongside the message so UI code can
// distinguish 401 (unauthenticated) from other failures and redirect to
// the login page.
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = r.statusText;
    try {
      const body = await r.json();
      if (body && typeof body.error === "string") msg = body.error;
    } catch {
      /* not json — fall through */
    }
    throw new ApiError(r.status, msg);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Build time-range query params. When from+to are provided, uses absolute
 *  window (`from=T1&to=T2`); otherwise falls back to relative `seconds=N`.
 *  Backend parseSeriesRange() accepts both forms. */
function _tp(seconds: number, from?: number, to?: number): string {
  if (from !== undefined && to !== undefined) {
    return `from=${from}&to=${to}`;
  }
  return `seconds=${seconds}`;
}

// ── daily report types ────────────────────────────────────────────────────

export const REPORT_SECTIONS = [
  { value: "bandwidth", label: "Bandwidth (peak/avg)" },
  { value: "top_ips",   label: "Top IP Talkers" },
  { value: "top_asn",   label: "Top ASN" },
  { value: "flows",     label: "Flows/s" },
  { value: "geo",       label: "IXP vs Domestic" },
  { value: "alerts",    label: "Alert Summary" },
  { value: "sensors",   label: "Sensor Summary" },
] as const;

export interface ReportConfig {
  id: number;
  name: string;
  enabled: boolean;
  send_at: string;       // "HH:MM:SS"
  window_type: "last24h" | "yesterday" | "custom";
  window_start: string;  // "HH:MM:SS" or ""
  window_end: string;    // "HH:MM:SS" or ""
  sections: string[];
  channel_ids: number[];
  keep_history: number;  // max history records to keep (default 30)
  created_at: string;
  updated_at: string;
}

export interface ReportConfigBody {
  name: string;
  enabled: boolean;
  send_at: string;
  window_type: string;
  window_start: string;
  window_end: string;
  sections: string[];
  channel_ids: number[];
  keep_history: number;
}

export interface ReportHistory {
  id: number;
  config_id: number;
  config_name: string;
  period_start: string;
  period_end: string;
  status: "sent" | "failed";
  summary: string;
  sent_at: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// api surface
// ---------------------------------------------------------------------------

export interface UserAuditEntry {
  ts_unix: number;
  user_id: number;
  username: string;
  role: string;
  action: string; // login | login_failed | logout | mutate
  method: string;
  path: string;
  status: number;
  ip: string;
  detail: string;
}
export interface UserAuditResp {
  total: number;
  entries: UserAuditEntry[];
  retention_days: number;
}

export const api = {
  // public
  health: () => request<Health>("/api/v1/health"),
  login: (username: string, password: string) =>
    request<User>("/api/v1/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  // auth-protected
  logout: () => request<{ ok: true }>("/api/v1/logout", { method: "POST" }),
  me: () => request<User>("/api/v1/me"),

  sensors: () => request<Sensor[]>("/api/v1/sensors"),
  sensor: (id: number) => request<SensorDetailResp>(`/api/v1/sensors/${id}`),
  /** Walk IF-MIB on the sensor's exporter and return the picker rows. */
  discoverInterfaces: (id: number) =>
    request<{ interfaces: DiscoveredIface[]; exporter: string }>(
      `/api/v1/sensors/${id}/discover-interfaces`
    ),
  createSensor: (body: SensorWriteReq) =>
    request<{ id: number }>("/api/v1/sensors", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSensor: (id: number, body: SensorWriteReq) =>
    request<{ id: number }>(`/api/v1/sensors/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteSensor: (id: number) =>
    request<{ ok: true }>(`/api/v1/sensors/${id}`, { method: "DELETE" }),

  decoders: () => request<Decoder[]>("/api/v1/decoders"),
  setDecoderEnabled: (id: number, enabled: boolean) =>
    request<{ id: number; enabled: boolean }>(`/api/v1/decoders/${id}`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
  /**
   * Batch-update all decoder enable flags in one request. The server
   * sets `enabled = id IN enabled_ids` atomically and triggers a
   * single sfsensor restart, regardless of how many rows flipped.
   * Used by the Decoders page Save button.
   */
  saveDecoders: (enabledIDs: number[]) =>
    request<{ enabled_count: number }>("/api/v1/decoders/batch", {
      method: "POST",
      body: JSON.stringify({ enabled_ids: enabledIDs }),
    }),

  zones: () => request<Zone[]>("/api/v1/zones"),
  createZone: (name: string, has_ipv6: boolean) =>
    request<{ id: number }>("/api/v1/zones", {
      method: "POST",
      body: JSON.stringify({ name, has_ipv6 }),
    }),
  updateZone: (zoneID: number, name: string, has_ipv6: boolean) =>
    request<{ ok: true }>(`/api/v1/zone/${zoneID}`, {
      method: "PUT",
      body: JSON.stringify({ name, has_ipv6 }),
    }),
  deleteZone: (zoneID: number, force?: boolean) =>
    request<{ ok: true }>(
      `/api/v1/zone/${zoneID}${force ? "?force=1" : ""}`,
      { method: "DELETE" }
    ),
  userAudit: (
    params: { limit?: number; offset?: number; user?: string; action?: string } = {}
  ) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    if (params.user) q.set("user", params.user);
    if (params.action) q.set("action", params.action);
    return request<UserAuditResp>(`/api/v1/admin/audit?${q.toString()}`);
  },
  setUserAuditRetention: (days: number) =>
    request<{ ok: true; retention_days: number }>(
      `/api/v1/admin/audit?retention_days=${days}`,
      { method: "POST" }
    ),
  zoneNodes: (zoneID: number) =>
    request<ZoneNode[]>(`/api/v1/zone/${zoneID}/nodes`),
  createZoneNode: (zoneID: number, body: IPAddrWriteReq) =>
    request<{ id: number }>(`/api/v1/zone/${zoneID}/nodes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateZoneNode: (zoneID: number, nodeID: number, body: IPAddrWriteReq) =>
    request<{ id: number }>(`/api/v1/zone/${zoneID}/nodes/${nodeID}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteZoneNode: (zoneID: number, nodeID: number) =>
    request<{ ok: true }>(`/api/v1/zone/${zoneID}/nodes/${nodeID}`, {
      method: "DELETE",
    }),
  bulkZoneNodes: (zoneID: number, prefixes: string[]) =>
    request<{ inserted: number }>(`/api/v1/zone/${zoneID}/nodes/bulk`, {
      method: "POST",
      body: JSON.stringify({ prefixes }),
    }),
  /** GET /api/v1/ipgroups — used by the prefix editor's IP Group
   *  AutoComplete to suggest existing labels. */
  ipgroups: () => request<IPGroup[]>(`/api/v1/ipgroups`),
  /** GET /api/v1/live/totals — current rolling-window bps/pps in/out
   *  + percent-change trend, used by the dashboard's TotalsBar cards.
   *  Optional scope mirrors the chart's View + Sensor pickers: view
   *  switches the `ip` measurement to `geo` (nix/ixp class); sensor
   *  narrows to one sFlow source. Omitted = network-wide total. */
  liveTotals: (opts?: { view?: "total" | "nix" | "ixp"; sensor?: number }) => {
    const p = new URLSearchParams();
    if (opts?.view && opts.view !== "total") p.set("view", opts.view);
    if (opts?.sensor != null) p.set("sensor", String(opts.sensor));
    const qs = p.toString();
    return request<LiveTotals>(`/api/v1/live/totals${qs ? `?${qs}` : ""}`);
  },
  /** GET /api/v1/live/sensors-summary — per-sensor current rates for
   *  the Sensors page table. */
  liveSensorsSummary: () =>
    request<LiveSensorSummary[]>(`/api/v1/live/sensors-summary`),
  /** POST /api/v1/flows/search — search the binary flow archive. */
  flowSearch: (body: FlowSearchReq) =>
    request<FlowSearchResp>(`/api/v1/flows/search`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  liveIP: (nodeID: number) => request<IPRates[]>(`/api/v1/live/ip/${nodeID}`),
  ipSearch: (q: string) =>
    request<IPSearchResp>(`/api/v1/ip?q=${encodeURIComponent(q)}`),
  ipNodeSeries: (nodeID: number, seconds = 600) =>
    request<IPSeriesResp>(`/api/v1/ip/${nodeID}/series?seconds=${seconds}`),
  ipHostSeries: (addr: string, seconds = 600) =>
    request<IPHostSeriesResp>(
      `/api/v1/ip/host-series?addr=${encodeURIComponent(addr)}&seconds=${seconds}`
    ),
  ipCIDRSeries: (prefix: string, seconds = 600) =>
    request<IPCIDRSeriesResp>(
      `/api/v1/ip/cidr-series?prefix=${encodeURIComponent(prefix)}&seconds=${seconds}`
    ),
  ipNodeProtoSeries: (nodeID: number, seconds = 600, from?: number, to?: number) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/${nodeID}/proto-series?${_tp(seconds, from, to)}`),
  /** Aggregate per-decoder in/out series across every ipaddr row in a zone. */
  zoneProtoSeries: (
    zoneID: number,
    seconds = 600,
    geoClass?: "nix" | "ixp",
    from?: number,
    to?: number
  ) =>
    request<IPProtoSeriesResp & { zone_id: number; name: string; node_ids: number[] }>(
      `/api/v1/zone/${zoneID}/proto-series?${_tp(seconds, from, to)}${
        geoClass ? `&class=${geoClass}` : ""
      }`
    ),
  ipHostProtoSeries: (addr: string, seconds = 600, from?: number, to?: number) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/host-proto-series?addr=${encodeURIComponent(addr)}&${_tp(seconds, from, to)}`),
  ipCIDRProtoSeries: (prefix: string, seconds = 600, from?: number, to?: number) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/cidr-proto-series?prefix=${encodeURIComponent(prefix)}&${_tp(seconds, from, to)}`),
  ipNodeGeoSeries: (nodeID: number, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<IPSeriesResp>(`/api/v1/ip/${nodeID}/geo-series?${_tp(seconds, from, to)}&class=${geoClass}`),
  ipHostGeoSeries: (addr: string, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<IPHostSeriesResp>(
      `/api/v1/ip/host-geo-series?addr=${encodeURIComponent(addr)}&${_tp(seconds, from, to)}&class=${geoClass}`
    ),
  ipCIDRGeoSeries: (prefix: string, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<{ prefix: string; points: SeriesPoint[] }>(
      `/api/v1/ip/cidr-geo-series?prefix=${encodeURIComponent(prefix)}&${_tp(seconds, from, to)}&class=${geoClass}`
    ),
  ipNodeGeoProtoSeries: (nodeID: number, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<IPProtoSeriesResp>(`/api/v1/ip/${nodeID}/geo-proto-series?${_tp(seconds, from, to)}&class=${geoClass}`),
  ipHostGeoProtoSeries: (addr: string, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<IPProtoSeriesResp>(
      `/api/v1/ip/host-geo-proto-series?addr=${encodeURIComponent(addr)}&${_tp(seconds, from, to)}&class=${geoClass}`
    ),
  ipCIDRGeoProtoSeries: (prefix: string, seconds = 600, geoClass: "nix" | "ixp", from?: number, to?: number) =>
    request<IPProtoSeriesResp>(
      `/api/v1/ip/cidr-geo-proto-series?prefix=${encodeURIComponent(prefix)}&${_tp(seconds, from, to)}&class=${geoClass}`
    ),
  liveTop: (
    metric: "bps" | "pps",
    dir: "in" | "out",
    limit: number,
    geo?: "ixp",
    filter?: TopFilter,
    sensorId?: number,
  ) => {
    // Compose optional scope filters into the query. Each is a server-side
    // filter — callers don't have to filter the response client-side. Empty
    // values are skipped so the URL stays compact for the common no-filter
    // case (Dashboard → top talkers widget). sensorId scopes the rollup to
    // one sFlow source (per-sensor dashboard).
    const parts = [`metric=${metric}`, `dir=${dir}`, `limit=${limit}`];
    if (geo) parts.push(`geo=${geo}`);
    if (filter?.cidr) parts.push(`cidr=${encodeURIComponent(filter.cidr)}`);
    if (filter?.ipgroup) parts.push(`ipgroup=${filter.ipgroup}`);
    if (filter?.node) parts.push(`node=${filter.node}`);
    if (filter?.zone) parts.push(`zone=${filter.zone}`);
    if (sensorId) parts.push(`sensor=${sensorId}`);
    return request<TopTalker[]>(`/api/v1/live/top?${parts.join("&")}`);
  },

  // Top remote ASNs ranked by bps/pps, scoped to a geo class (ixp default) and direction.
  // sensorId scopes the rollup to one sFlow source (per-sensor dashboard).
  liveTopASN: (
    geo: "nix" | "ixp",
    dir: "in" | "out",
    metric: "bps" | "pps" = "bps",
    limit = 10,
    sensorId?: number,
  ) =>
    request<TopASNRow[]>(
      `/api/v1/live/top/asn?geo=${geo}&dir=${dir}&metric=${metric}&limit=${limit}` +
        (sensorId ? `&sensor=${sensorId}` : "")
    ),

  // Each series endpoint supports two time-window modes:
  //  - relative: pass `seconds` (default 600) → server uses time > now() - Ns
  //  - absolute: pass `range = { start, end }` (unix seconds) → server uses
  //    a fixed time >= A AND time < B window. Used by the LiveChart's
  //    "Custom range" picker so the operator can compare a specific
  //    historical window with the same window in Wansight.
  // When `range` is set, `seconds` is ignored.
  seriesGlobal: (seconds = 600, range?: { start: number; end: number }) => {
    const q = range
      ? `start_unix=${range.start}&end_unix=${range.end}`
      : `seconds=${seconds}`;
    return request<EnvelopePoint[]>(`/api/v1/series/global?${q}`);
  },

  seriesSensors: (seconds = 600, range?: { start: number; end: number }) => {
    const q = range
      ? `start_unix=${range.start}&end_unix=${range.end}`
      : `seconds=${seconds}`;
    return request<SensorSeries[]>(`/api/v1/series/sensors?${q}`);
  },

  // Returns per-sensor series for the selected geo class (nix=domestic, ixp=international).
  // Same shape as seriesSensors so LiveChart renders it identically.
  seriesGeo: (
    seconds = 600,
    geoClass: "nix" | "ixp",
    range?: { start: number; end: number }
  ) => {
    const q = range
      ? `start_unix=${range.start}&end_unix=${range.end}&class=${geoClass}`
      : `seconds=${seconds}&class=${geoClass}`;
    return request<SensorSeries[]>(`/api/v1/series/geo?${q}`);
  },


  // ── user management ────────────────────────────────────────────────────────
  listUsers: () =>
    request<{ id: number; username: string; role: string }[]>("/api/v1/users"),

  createUser: (username: string, password: string, role: string) =>
    request<{ id: number; username: string; role: string }>("/api/v1/users", {
      method: "POST",
      body: JSON.stringify({ username, password, role }),
    }),

  updateUserRole: (id: number, role: string) =>
    request<{ ok: boolean }>(`/api/v1/users/${id}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    }),

  deleteUser: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/users/${id}`, { method: "DELETE" }),

  changePassword: (opts: {
    target_username?: string;
    current_password?: string;
    new_password: string;
  }) =>
    request<{ ok: boolean }>("/api/v1/users/password", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // ── notifications ──────────────────────────────────────────────────────────
  notifChannels: () =>
    request<NotifChannel[]>("/api/v1/notifications/channels"),
  createNotifChannel: (body: Omit<NotifChannel, "id" | "created_at">) =>
    request<{ id: number }>("/api/v1/notifications/channels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateNotifChannel: (id: number, body: Omit<NotifChannel, "id" | "created_at">) =>
    request<{ ok: boolean }>(`/api/v1/notifications/channels/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteNotifChannel: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/channels/${id}`, {
      method: "DELETE",
    }),
  testNotifChannel: (id: number, event?: "resolve") =>
    request<{ ok: boolean }>(
      `/api/v1/notifications/channels/${id}/test${event ? `?event=${event}` : ""}`,
      { method: "POST" },
    ),

  notifRules: () =>
    request<AlertRule[]>("/api/v1/notifications/rules"),
  createNotifRule: (body: Omit<AlertRule, "id" | "channel_id" | "channel_name" | "channel_names" | "status" | "last_fired" | "zone_filter_names" | "decoder_name" | "forensics">) =>
    request<{ id: number }>("/api/v1/notifications/rules", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateNotifRule: (id: number, body: Omit<AlertRule, "id" | "channel_id" | "channel_name" | "channel_names" | "status" | "last_fired" | "zone_filter_names" | "decoder_name" | "forensics">) =>
    request<{ ok: boolean }>(`/api/v1/notifications/rules/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteNotifRule: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/rules/${id}`, {
      method: "DELETE",
    }),

  /** Render a message template against synthetic dummy data so the
   * operator can preview the output before saving. `scenario` picks the
   * shape of the dummy payload (firing aggregate, resolved, SYN-flood,
   * batch, no-forensics). The response is the final HTML-escaped
   * Telegram-flavored string. */
  previewNotifTemplate: (template: string, scenario: string) =>
    request<{ rendered: string }>("/api/v1/notifications/template/preview", {
      method: "POST",
      body: JSON.stringify({ template, scenario }),
    }),

  notifHistory: (params?: { limit?: number; rule_id?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.rule_id) qs.set("rule_id", String(params.rule_id));
    const q = qs.toString();
    return request<NotifHistoryItem[]>(`/api/v1/notifications/history${q ? "?" + q : ""}`);
  },

  notifHistoryRetention: () =>
    request<{ days: number }>("/api/v1/notifications/history/retention"),

  setNotifHistoryRetention: (days: number) =>
    request<{ days: number }>("/api/v1/notifications/history/retention", {
      method: "PUT",
      body: JSON.stringify({ days }),
    }),

  flushNotifHistory: () =>
    request<{ ok: boolean; deleted: number }>("/api/v1/notifications/history/flush", {
      method: "POST",
    }),

  // ── CDN prefix matcher (Google + Cloudflare) ──────────────────────────────
  cdnStats: () =>
    request<{
      google_count: number;
      cloudflare_count: number;
      last_update: string; // ISO timestamp; epoch zero = "never refreshed"
      last_error?: string;
      persist_path?: string;
      auto_update: boolean;
    }>("/api/v1/cdn/stats"),

  cdnRefresh: () =>
    request<{
      ok: boolean;
      google_count: number;
      cloudflare_count: number;
      last_update: string;
    }>("/api/v1/cdn/refresh", { method: "POST" }),

  cdnAutoUpdate: () =>
    request<{ enabled: boolean }>("/api/v1/cdn/auto-update"),

  setCdnAutoUpdate: (enabled: boolean) =>
    request<{ ok: boolean; enabled: boolean }>("/api/v1/cdn/auto-update", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),

  // ── admin: data retention & flush ──────────────────────────────────────────
  getRetention: () =>
    request<{ days: number }>("/api/v1/admin/retention"),

  setRetention: (days: number) => {
    const body = new URLSearchParams({ days: String(days) });
    return request<{ ok: boolean; days: number }>("/api/v1/admin/retention", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  flushData: (olderThanSeconds: number) => {
    const body = new URLSearchParams({ older_than: String(olderThanSeconds) });
    return request<{ ok: boolean; older_than_seconds: number }>("/api/v1/admin/flush", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  // ── admin: wgflow archive retention ───────────────────────────────────────
  wgflowStats: () =>
    request<{
      retention_days: number;
      dirs: Array<{
        path: string;
        file_count: number;
        total_bytes: number;
        oldest_file?: string;
        newest_file?: string;
      }>;
    }>("/api/v1/admin/wgflow/stats"),

  wgflowSetRetention: (days: number) => {
    const body = new URLSearchParams({ days: String(days) });
    return request<{ ok: boolean; retention_days: number }>("/api/v1/admin/wgflow/retention", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  wgflowFlush: (olderThanDays: number) => {
    const body = new URLSearchParams({ older_than_days: String(olderThanDays) });
    return request<{ ok: boolean; deleted_files: number; freed_bytes: number }>("/api/v1/admin/wgflow/flush", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
  },

  // ── update management ──────────────────────────────────────────────────────
  updateCheck: () =>
    request<UpdateCheck>("/api/v1/update/check"),

  updateApply: (version?: string) =>
    request<{ ok: string; message: string }>("/api/v1/update/apply", {
      method: "POST",
      body: JSON.stringify({ version: version ?? "" }),
    }),

  updateStatus: () =>
    request<UpdateStatus>("/api/v1/update/status"),

  // ── external API: key management & settings ────────────────────────────────
  listAPIKeys: () =>
    request<APIKeyRow[]>("/api/v1/apikeys"),

  createAPIKey: (body: {
    name: string;
    scopes: Array<"read" | "write" | "admin">;
    expires_at?: number | null; // unix seconds
  }) =>
    request<CreateAPIKeyResp>("/api/v1/apikeys", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  revokeAPIKey: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/apikeys/${id}`, { method: "DELETE" }),

  /** Permanently delete a revoked key from the database. Backend refuses
   * with 409 if the key is still enabled — call revokeAPIKey first. Useful
   * for cleaning up old test keys from the audit list. */
  purgeAPIKey: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/apikeys/${id}/purge`, { method: "POST" }),

  apiSettings: () =>
    request<APISettings>("/api/v1/settings/api"),

  updateAPISettings: (body: APISettings) =>
    request<APISettings>("/api/v1/settings/api", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ── live rolling window ────────────────────────────────────────────────────
  liveSettings: () =>
    request<LiveSettings>("/api/v1/settings/live"),

  updateLiveSettings: (body: LiveSettings) =>
    request<LiveSettings>("/api/v1/settings/live", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  liveRecommendation: () =>
    request<LiveRecommendation>("/api/v1/settings/live/recommendation"),

  // ── geo feature toggles ────────────────────────────────────────────────────
  geoSettings: () =>
    request<GeoSettings>("/api/v1/settings/geo"),

  updateGeoSettings: (body: GeoSettings) =>
    request<GeoSettings>("/api/v1/settings/geo", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ── GeoIP / MaxMind MMDB ──────────────────────────────────────────────────
  geoMMDBStatus: () =>
    request<GeoMMDBStatus>("/api/v1/settings/geo/mmdb"),

  updateGeoMMDB: (body: GeoMMDBPutBody) =>
    request<GeoMMDBStatus>("/api/v1/settings/geo/mmdb", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  triggerGeoMMDBUpdate: () =>
    request<{ status: string }>("/api/v1/settings/geo/mmdb/update", {
      method: "POST",
    }),

  apiKeyUsage: (opts?: { key_id?: number; limit?: number }) => {
    const p = new URLSearchParams();
    if (opts?.key_id != null) p.set("key_id", String(opts.key_id));
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<APIAccessLogRow[]>(`/api/v1/apikeys/usage${qs ? "?" + qs : ""}`);
  },

  flushAPIAuditLog: () =>
    request<{ ok: boolean; deleted: number }>("/api/v1/apikeys/usage/flush", {
      method: "POST",
    }),

  // ── license management ─────────────────────────────────────────────────────
  licenseStatus: () =>
    request<LicenseStatus>("/api/v1/license"),

  activateLicense: (key: string) =>
    request<LicenseStatus>("/api/v1/license", {
      method: "POST",
      body: JSON.stringify({ key }),
    }),

  removeLicense: () =>
    request<LicenseStatus>("/api/v1/license", { method: "DELETE" }),

  // ── daily report ──────────────────────────────────────────────────────────
  reportConfigs: () =>
    request<ReportConfig[]>("/api/v1/reports/configs"),

  createReportConfig: (body: ReportConfigBody) =>
    request<{ id: number }>("/api/v1/reports/configs", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateReportConfig: (id: number, body: ReportConfigBody) =>
    request<{ ok: boolean }>(`/api/v1/reports/configs/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteReportConfig: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/reports/configs/${id}`, {
      method: "DELETE",
    }),

  sendReport: (id: number, opts?: { start_unix?: number; end_unix?: number }) =>
    request<{ ok: boolean; async: boolean }>(`/api/v1/reports/configs/${id}/send`, {
      method: "POST",
      body: JSON.stringify(opts ?? {}),
    }),

  reportHistory: (opts?: { config_id?: number; limit?: number }) => {
    const p = new URLSearchParams();
    if (opts?.config_id != null) p.set("config_id", String(opts.config_id));
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<ReportHistory[]>(`/api/v1/reports/history${qs ? "?" + qs : ""}`);
  },

  resendReport: (historyId: number) =>
    request<{ ok: boolean; async: boolean }>(`/api/v1/reports/history/${historyId}/resend`, {
      method: "POST",
    }),
};
